import { supabase } from './supabaseClient';
import {
  WorkflowTemplate,
  WorkflowTemplateRow,
  TemplateMatchingContext,
  ActionType,
  JourneyStage,
  JourneyMarket,
  JourneyPersona,
  TemplateContent,
  SurveyTemplateContent,
  TaskTemplateContent,
  TeamTemplateContent,
  PreReleaseTemplateContent,
  workflowTemplateRowToWorkflowTemplate
} from '../types/journey';
import { WorkflowTemplateGenerationService } from './workflowTemplateGenerationService';

/**
 * Service for managing workflow templates - storage, matching, and customization
 */
export class WorkflowTemplateService {

  /**
   * Gets all active templates from the database
   */
  static async getAllTemplates(): Promise<WorkflowTemplate[]> {
    const { data, error } = await supabase
      .from('workflow_templates')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch templates: ${error.message}`);
    }

    return data.map(workflowTemplateRowToWorkflowTemplate);
  }

  /**
   * Gets templates by type
   */
  static async getTemplatesByType(type: ActionType): Promise<WorkflowTemplate[]> {
    const { data, error } = await supabase
      .from('workflow_templates')
      .select('*')
      .eq('type', type)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch ${type} templates: ${error.message}`);
    }

    return data.map(workflowTemplateRowToWorkflowTemplate);
  }

  /**
   * Gets a specific template by ID
   */
  static async getTemplateById(templateId: string): Promise<WorkflowTemplate | null> {
    const { data, error } = await supabase
      .from('workflow_templates')
      .select('*')
      .eq('id', templateId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Template not found
      }
      throw new Error(`Failed to fetch template: ${error.message}`);
    }

    return workflowTemplateRowToWorkflowTemplate(data);
  }

  /**
   * Finds the best matching template for given context
   * Uses enhanced template engine with persona-based matching
   */
  static async findMatchingTemplate(context: TemplateMatchingContext): Promise<WorkflowTemplate | null> {
    console.log('🎯 findMatchingTemplate called with context:', context);
    
    try {
      // Try enhanced template matching first
      console.log('🔍 Trying enhanced template matching...');
      const { EnhancedTemplateEngine } = await import('./enhancedTemplateEngine');
      const enhancedTemplate = await EnhancedTemplateEngine.matchTemplate(context);
      
      if (enhancedTemplate) {
        console.log('✅ Found enhanced template:', enhancedTemplate.title);
        return enhancedTemplate;
      }
    } catch (error) {
      console.warn('❌ Enhanced template matching failed, falling back to basic matching:', error);
    }

    // Fallback to original logic for backward compatibility
    console.log('🔄 Falling back to basic template matching...');
    return await this.findMatchingTemplateBasic(context);
  }

  /**
   * Basic template matching logic (original implementation)
   */
  private static async findMatchingTemplateBasic(context: TemplateMatchingContext): Promise<WorkflowTemplate | null> {
    // First try to get contextually relevant templates from the generation service
    try {
      console.log('🔍 Trying contextual template lookup...');
      const contextualTemplates = await WorkflowTemplateGenerationService.getTemplatesByContext(context);
      console.log('📋 Found contextual templates:', contextualTemplates.length);
      
      if (contextualTemplates.length > 0) {
        // Return the most specific template (most conditions)
        const bestTemplate = contextualTemplates.reduce((best, current) => 
          current.conditions.length > best.conditions.length ? current : best
        );
        console.log('✅ Selected best contextual template:', bestTemplate.title);
        return bestTemplate;
      }
    } catch (error) {
      console.warn('❌ Failed to get contextual templates, falling back to basic matching:', error);
    }

    const templates = await this.getTemplatesByType(context.actionType);
    console.log('📋 Found templates by type:', templates.length);
    
    // Find templates that match all conditions
    const matchingTemplates = templates.filter(template => {
      const matches = this.evaluateTemplateConditions(template, context);
      console.log(`🔍 Template "${template.title}" matches:`, matches);
      return matches;
    });

    console.log('📋 Matching templates after filtering:', matchingTemplates.length);

    if (matchingTemplates.length === 0) {
      console.log('🔄 No matching templates, trying fallback...');
      // Try to find a fallback template with fewer conditions
      const fallback = await this.findFallbackTemplate(context);
      if (fallback) {
        console.log('✅ Found fallback template:', fallback.title);
      } else {
        console.log('❌ No fallback template found');
      }
      return fallback;
    }

    // Return the most specific template (most conditions)
    const bestTemplate = matchingTemplates.reduce((best, current) => 
      current.conditions.length > best.conditions.length ? current : best
    );
    
    console.log('✅ Selected best matching template:', bestTemplate.title);
    return bestTemplate;
  }

  /**
   * Evaluates if a template matches the given context
   */
  private static evaluateTemplateConditions(
    template: WorkflowTemplate, 
    context: TemplateMatchingContext
  ): boolean {
    return template.conditions.every(condition => 
      this.evaluateCondition(condition, context)
    );
  }

  /**
   * Evaluates a single condition string against context
   */
  static evaluateCondition(condition: string, context: TemplateMatchingContext): boolean {
    try {
      // Parse condition like "stage == 'Beginner'" or "persona == 'Influencer'"
      const match = condition.match(/^(\w+)\s*(==|!=)\s*["']([^"']+)["']$/);
      if (!match) {
        console.warn(`Invalid condition format: ${condition}`);
        return false;
      }

      const [, field, operator, value] = match;
      const contextValue = (context as any)[field];

      if (operator === '==') {
        return contextValue === value;
      } else if (operator === '!=') {
        return contextValue !== value;
      }

      return false;
    } catch (error) {
      console.error(`Error evaluating condition "${condition}":`, error);
      return false;
    }
  }

  /**
   * Finds a fallback template when no exact match is found
   */
  private static async findFallbackTemplate(context: TemplateMatchingContext): Promise<WorkflowTemplate | null> {
    const templates = await this.getTemplatesByType(context.actionType);
    
    if (templates.length === 0) {
      return null;
    }
    
    // Try to find templates with partial matches, prioritizing stage and market
    const partialMatches = templates.filter(template => {
      const stageMatch = template.conditions.some(condition => 
        condition.includes('stage') && this.evaluateCondition(condition, context)
      );
      const marketMatch = template.conditions.some(condition => 
        condition.includes('market') && this.evaluateCondition(condition, context)
      );
      
      return stageMatch || marketMatch;
    });

    if (partialMatches.length > 0) {
      return partialMatches[0];
    }

    // Return the first generic template (fewest conditions) as last resort
    return templates.reduce((fallback, current) => 
      current.conditions.length < fallback.conditions.length ? current : fallback
    );
  }

  /**
   * Creates a preview of what the template will generate
   */
  static generateTemplatePreview(template: WorkflowTemplate): TemplatePreview {
    const basePreview = {
      id: template.id,
      type: template.type,
      title: template.title,
      description: this.getTemplateDescription(template)
    };

    switch (template.type) {
      case 'survey':
        const surveyContent = template.content as SurveyTemplateContent;
        return {
          ...basePreview,
          preview: {
            questionCount: surveyContent.questions?.length || 0,
            sampleQuestions: surveyContent.questions?.slice(0, 3) || [],
            targetResponses: surveyContent.targetResponses || 20
          }
        };

      case 'task':
        const taskContent = template.content as TaskTemplateContent;
        return {
          ...basePreview,
          preview: {
            checklistItems: taskContent.checklist?.length || 0,
            sampleItems: taskContent.checklist?.slice(0, 3) || [],
            expectedOutputs: taskContent.expectedOutputs?.length || 0,
            timeEstimate: taskContent.timeEstimate || 'Not specified'
          }
        };

      case 'team':
        const teamContent = template.content as TeamTemplateContent;
        return {
          ...basePreview,
          preview: {
            roleCount: teamContent.roles?.length || 0,
            roles: teamContent.roles?.map(role => ({
              title: role.title,
              skillCount: role.skills?.length || 0,
              timeCommitment: role.timeCommitment || 'Not specified'
            })) || []
          }
        };

      case 'preRelease':
        const preReleaseContent = template.content as PreReleaseTemplateContent;
        return {
          ...basePreview,
          preview: {
            headline: preReleaseContent.headline || 'Coming Soon',
            featureCount: preReleaseContent.features?.length || 0,
            targetSignups: preReleaseContent.targetSignups || 100,
            goalCount: preReleaseContent.launchGoals?.length || 0
          }
        };

      default:
        return {
          ...basePreview,
          preview: {
            contentKeys: Object.keys(template.content || {})
          }
        };
    }
  }

  /**
   * Gets a user-friendly description for the template
   */
  private static getTemplateDescription(template: WorkflowTemplate): string {
    const content = template.content as any;
    
    if (content.description) {
      return content.description;
    }

    // Generate description based on template type
    switch (template.type) {
      case 'survey':
        const questionCount = content.questions?.length || 0;
        return `Survey template with ${questionCount} questions to gather insights`;
      
      case 'task':
        const itemCount = content.checklist?.length || 0;
        return `Task template with ${itemCount} checklist items to guide implementation`;
      
      case 'team':
        const roleCount = content.roles?.length || 0;
        return `Team formation template with ${roleCount} role definitions`;
      
      case 'preRelease':
        return 'Pre-release template for building anticipation and collecting early users';
      
      default:
        return 'Template for workflow automation';
    }
  }

  /**
   * Customizes a template with user-specific modifications
   */
  static customizeTemplate(
    template: WorkflowTemplate, 
    customizations: TemplateCustomizations
  ): CustomizedTemplate {
    const customizedContent = { ...template.content };

    switch (template.type) {
      case 'survey':
        if (customizations.survey) {
          const surveyContent = customizedContent as SurveyTemplateContent;
          if (customizations.survey.title) {
            surveyContent.title = customizations.survey.title;
          }
          if (customizations.survey.description) {
            surveyContent.description = customizations.survey.description;
          }
          if (customizations.survey.questions) {
            surveyContent.questions = customizations.survey.questions;
          }
          if (customizations.survey.targetResponses) {
            surveyContent.targetResponses = customizations.survey.targetResponses;
          }
        }
        break;

      case 'task':
        if (customizations.task) {
          const taskContent = customizedContent as TaskTemplateContent;
          if (customizations.task.title) {
            taskContent.title = customizations.task.title;
          }
          if (customizations.task.description) {
            taskContent.description = customizations.task.description;
          }
          if (customizations.task.checklist) {
            taskContent.checklist = customizations.task.checklist;
          }
          if (customizations.task.expectedOutputs) {
            taskContent.expectedOutputs = customizations.task.expectedOutputs;
          }
        }
        break;

      case 'team':
        if (customizations.team) {
          const teamContent = customizedContent as TeamTemplateContent;
          if (customizations.team.title) {
            teamContent.title = customizations.team.title;
          }
          if (customizations.team.description) {
            teamContent.description = customizations.team.description;
          }
          if (customizations.team.roles) {
            teamContent.roles = customizations.team.roles;
          }
        }
        break;

      case 'preRelease':
        if (customizations.preRelease) {
          const preReleaseContent = customizedContent as PreReleaseTemplateContent;
          if (customizations.preRelease.title) {
            preReleaseContent.title = customizations.preRelease.title;
          }
          if (customizations.preRelease.description) {
            preReleaseContent.description = customizations.preRelease.description;
          }
          if (customizations.preRelease.headline) {
            preReleaseContent.headline = customizations.preRelease.headline;
          }
          if (customizations.preRelease.features) {
            preReleaseContent.features = customizations.preRelease.features;
          }
          if (customizations.preRelease.targetSignups) {
            preReleaseContent.targetSignups = customizations.preRelease.targetSignups;
          }
          if (customizations.preRelease.launchGoals) {
            preReleaseContent.launchGoals = customizations.preRelease.launchGoals;
          }
        }
        break;
    }

    return {
      originalTemplate: template,
      customizedContent,
      customizations,
      createdAt: new Date()
    };
  }

  /**
   * Validates template content structure
   */
  static validateTemplateContent(type: ActionType, content: any): ValidationResult {
    const errors: string[] = [];

    switch (type) {
      case 'survey':
        if (!content.title) errors.push('Survey title is required');
        if (!content.questions || !Array.isArray(content.questions)) {
          errors.push('Survey questions array is required');
        } else if (content.questions.length === 0) {
          errors.push('At least one question is required');
        }
        break;

      case 'task':
        if (!content.title) errors.push('Task title is required');
        if (!content.checklist || !Array.isArray(content.checklist)) {
          errors.push('Task checklist array is required');
        } else if (content.checklist.length === 0) {
          errors.push('At least one checklist item is required');
        }
        break;

      case 'team':
        if (!content.title) errors.push('Team title is required');
        if (!content.roles || !Array.isArray(content.roles)) {
          errors.push('Team roles array is required');
        } else if (content.roles.length === 0) {
          errors.push('At least one role is required');
        } else {
          content.roles.forEach((role: any, index: number) => {
            if (!role.title) errors.push(`Role ${index + 1} title is required`);
            if (!role.description) errors.push(`Role ${index + 1} description is required`);
          });
        }
        break;

      case 'preRelease':
        if (!content.title) errors.push('Pre-release title is required');
        if (!content.headline) errors.push('Pre-release headline is required');
        if (!content.features || !Array.isArray(content.features)) {
          errors.push('Pre-release features array is required');
        } else if (content.features.length === 0) {
          errors.push('At least one feature is required');
        }
        break;
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Gets comprehensive template statistics and coverage information
   */
  static async getTemplateCoverage(): Promise<TemplateCoverageReport> {
    try {
      const stats = await WorkflowTemplateGenerationService.getTemplateStatistics();
      
      // Calculate coverage percentages
      const expectedCombinations = {
        stages: 3, // Beginner, Have Idea, In Progress
        markets: 4, // E-commerce, IT/Tech, Social Media/Influencer, Other
        personas: 5, // Idea-Seeker, Validating Founder, Builder with Gaps, Influencer, Operator
        actionTypes: 4 // survey, task, team, preRelease
      };

      const stageCoverage = (Object.keys(stats.templatesByStage).length / expectedCombinations.stages) * 100;
      const marketCoverage = (Object.keys(stats.templatesByMarket).length / expectedCombinations.markets) * 100;
      const personaCoverage = (Object.keys(stats.templatesByPersona).length / expectedCombinations.personas) * 100;
      const actionTypeCoverage = (Object.keys(stats.templatesByType).length / expectedCombinations.actionTypes) * 100;

      return {
        totalTemplates: stats.totalTemplates,
        templatesByType: stats.templatesByType,
        templatesByMarket: stats.templatesByMarket,
        templatesByStage: stats.templatesByStage,
        templatesByPersona: stats.templatesByPersona,
        coverage: {
          stage: Math.round(stageCoverage),
          market: Math.round(marketCoverage),
          persona: Math.round(personaCoverage),
          actionType: Math.round(actionTypeCoverage),
          overall: Math.round((stageCoverage + marketCoverage + personaCoverage + actionTypeCoverage) / 4)
        },
        recommendations: this.generateCoverageRecommendations(stats)
      };
    } catch (error) {
      throw new Error(`Failed to get template coverage: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Generates recommendations for improving template coverage
   */
  private static generateCoverageRecommendations(stats: any): string[] {
    const recommendations: string[] = [];

    // Check for missing action types
    const actionTypes: ActionType[] = ['survey', 'task', 'team', 'preRelease', 'ai', 'launchAssets', 'instruction'];
    const missingActionTypes = actionTypes.filter(type => !stats.templatesByType[type]);
    if (missingActionTypes.length > 0) {
      recommendations.push(`Add templates for missing action types: ${missingActionTypes.join(', ')}`);
    }

    // Check for underrepresented markets
    const markets: JourneyMarket[] = ['E-commerce', 'IT/Tech', 'Social Media/Influencer', 'Other'];
    const underrepresentedMarkets = markets.filter(market => 
      !stats.templatesByMarket[market] || stats.templatesByMarket[market] < 2
    );
    if (underrepresentedMarkets.length > 0) {
      recommendations.push(`Add more templates for markets: ${underrepresentedMarkets.join(', ')}`);
    }

    // Check for underrepresented stages
    const stages: JourneyStage[] = ['Beginner', 'Have Idea', 'In Progress'];
    const underrepresentedStages = stages.filter(stage => 
      !stats.templatesByStage[stage] || stats.templatesByStage[stage] < 2
    );
    if (underrepresentedStages.length > 0) {
      recommendations.push(`Add more templates for stages: ${underrepresentedStages.join(', ')}`);
    }

    // Check for underrepresented personas
    const personas: JourneyPersona[] = ['skilled-maker', 'expert-service', 'founder-plan'];
    const underrepresentedPersonas = personas.filter(persona => 
      !stats.templatesByPersona[persona] || stats.templatesByPersona[persona] < 1
    );
    if (underrepresentedPersonas.length > 0) {
      recommendations.push(`Add templates for personas: ${underrepresentedPersonas.join(', ')}`);
    }

    if (recommendations.length === 0) {
      recommendations.push('Template coverage looks comprehensive! Consider adding specialized templates for niche use cases.');
    }

    return recommendations;
  }
}

// Supporting types for template functionality
export interface TemplatePreview {
  id: string;
  type: ActionType;
  title: string;
  description: string;
  preview: any; // Type-specific preview data
}

export interface TemplateCustomizations {
  survey?: Partial<SurveyTemplateContent>;
  task?: Partial<TaskTemplateContent>;
  team?: Partial<TeamTemplateContent>;
  preRelease?: Partial<PreReleaseTemplateContent>;
}

export interface CustomizedTemplate {
  originalTemplate: WorkflowTemplate;
  customizedContent: TemplateContent;
  customizations: TemplateCustomizations;
  createdAt: Date;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface TemplateCoverageReport {
  totalTemplates: number;
  templatesByType: Record<ActionType, number>;
  templatesByMarket: Record<JourneyMarket, number>;
  templatesByStage: Record<JourneyStage, number>;
  templatesByPersona: Record<JourneyPersona, number>;
  coverage: {
    stage: number;
    market: number;
    persona: number;
    actionType: number;
    overall: number;
  };
  recommendations: string[];
}